import { useThree } from '@react-three/fiber';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type Texture,
  Group,
  InstancedBufferAttribute,
  PlaneGeometry,
  ShaderMaterial,
  Sphere,
  TextureLoader,
  Vector3,
} from 'three';

import { usePromiseStore } from 'state/promiseStore';
import type { GroupProps } from 'types/r3f';
import { publicAssets } from 'utils/gcs';

import fragmentShader from './quad-cloud.frag';
import vertexShader from './quad-cloud.vert';

const CLOUD_URL = publicAssets('/cloud-bushfire.png');
const MAX_LIGHTS = 24; // Update the value also in the clouds.frag file
const textureLoader = new TextureLoader();

export interface BushfireQuadCloudRef {
  position: Vector3;
  scale: number;
  opacity: number;
  userData: Record<string, any>;
  isLoaded: boolean;
}

export interface BushfireQuadCloudProps extends GroupProps {
  /** How many segments or particles the cloud will have, default: 10 */
  segmentCount?: number;
  /** The box3 bounds of the cloud, default: (3, 3, 3) */
  bounds?: Vector3;
  /** The volume/thickness of the segments, default: 6 */
  volume?: number;
  /** Opacity, default: 1 */
  opacity?: number;
  baseLight?: number;
}

export const BushfireQuadCloud = forwardRef<
  BushfireQuadCloudRef,
  BushfireQuadCloudProps
>(
  (
    {
      segmentCount = 10,
      bounds = new Vector3(3, 3, 3),
      volume = 3,
      opacity = 1,
      baseLight = 1,
      ...props
    },
    ref
  ) => {
    const exec = usePromiseStore(s => s.exec);
    const gl = useThree(state => state.gl);
    const groupRef = useRef<Group>(null!);
    const materialRef = useRef<ShaderMaterial>(null!);

    const [texture, setTexture] = useState<Texture | null>(null);

    useEffect(() => {
      exec(CLOUD_URL, () => textureLoader.loadAsync(CLOUD_URL)).then(tex => {
        setTexture(tex);
      });
    }, [exec]);

    const isLoadedRef = useRef(false);

    useEffect(() => {
      isLoadedRef.current = !!texture;
    }, [texture]);

    useImperativeHandle(
      ref,
      () => ({
        position: groupRef.current.position,
        userData: groupRef.current.userData,
        set scale(n: number) {
          const mat = materialRef.current;
          if (!mat) return;
          mat.uniforms.uScale.value = n;
        },
        get scale() {
          const mat = materialRef.current;
          if (!mat) return 0;
          return mat.uniforms.uScale.value;
        },
        set opacity(n: number) {
          const mat = materialRef.current;
          if (!mat) return;
          mat.uniforms.uOpacity.value = n;
        },
        get opacity() {
          const mat = materialRef.current;
          if (!mat) return 0;
          return mat.uniforms.uOpacity.value;
        },
        get isLoaded() {
          return isLoadedRef.current;
        },
      }),
      []
    );

    const geometry = useMemo(() => {
      const pg = new PlaneGeometry(2, 2);

      pg.boundingSphere = new Sphere(
        new Vector3(0, 0, 0),
        Math.max(bounds.x, bounds.y, bounds.z) + volume / 2
      );

      const offsets = new Float32Array(segmentCount * 3);
      const sizes = new Float32Array(segmentCount);

      for (let i = 0; i < segmentCount; i++) {
        const i3 = i * 3;
        offsets[i3] = bounds.x * (Math.random() * 2 - 1);
        offsets[i3 + 1] = bounds.y * (Math.random() * 2 - 1);
        offsets[i3 + 2] = bounds.z * (Math.random() * 2 - 1);
        sizes[i] = Math.max(0.2, Math.random()) * volume * gl.getPixelRatio();
      }

      pg.setAttribute('aOffset', new InstancedBufferAttribute(offsets, 3));
      pg.setAttribute('aSize', new InstancedBufferAttribute(sizes, 1));
      return pg;
    }, [gl, segmentCount, bounds, volume]);

    return (
      <group ref={groupRef} {...props}>
        {texture && (
          <instancedMesh
            args={[undefined, undefined, segmentCount]}
            geometry={geometry}
            layers={0}
          >
            <shaderMaterial
              ref={materialRef}
              vertexShader={vertexShader}
              fragmentShader={fragmentShader}
              uniforms={{
                uTexture: { value: texture },
                uOpacity: { value: opacity },
                uBaseLight: { value: baseLight },
                uLightPositions: { value: new Float32Array(MAX_LIGHTS * 3) },
                uLightIntensities: { value: new Float32Array(MAX_LIGHTS) },
                uScale: { value: 1 },
              }}
              transparent={true}
              depthWrite={false}
            />
          </instancedMesh>
        )}
      </group>
    );
  }
);
BushfireQuadCloud.displayName = 'BushfireQuadCloud';
